import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import * as R from "ramda";
import { Box, Flex, Grid, GridItem, VStack, Text } from "@chakra-ui/react";
import { Money } from "components/Money";
import MoreOptionsMenu from "./MoreOptionsMenu";
import PayoutOptionSelector from "./PayoutOptionSelector";
import { MethodClassIcon } from "components/Icons";
import { useCurrencyFormatter } from "components/FormattedMoney/useCurrencyFormatter";
import { FormattedMoney } from "components/FormattedMoney";
import getDisplayFeeString from "utils/getDisplayFeeString";
import PaymentMethodTags from "./PaymentMethodTags";

const isWalletType = R.propEq("provider", "wallet");

const getProviderFlatRateFeeInDollars = (amount = 0) => {
  const inDollars = amount / 100; // existing logic wants a dollar value
  const hasRemainder = !!(inDollars % 1);
  if (hasRemainder) {
    return inDollars; // this has a remainder and we want to show the full amount
  }
  return Math.floor(inDollars); // no remainder so let's reduce the length when it's converted to a string
};

const getPayoutOptionStr = (payoutOption, USDFormatter, formatMessage) => {
  // const minDays = payoutOption.minimum_business_days;
  // const maxDays = payoutOption.maximum_business_days;

  // Masspay fees in dollars
  const fee =
    payoutOption.fee ||
    (Number.isInteger(payoutOption.provider_flat_rate_fee) &&
    getProviderFlatRateFeeInDollars(payoutOption.provider_flat_rate_fee));
  // Our fees in percentage
  const feePercent = payoutOption.fee_percentage;

  let str = "";
  // get the day range and add it to the return string
  /*--- TODO: REMOVING FOR NOW TILL WE SORT OUT WHAT WE WANT THIS TO SAY ---*/
  // if (minDays === maxDays) {
  //   if (minDays === 0) {
  //     str = formatMessage({
  //       defaultMessage: "Same day",
  //       description: "date text",
  //     });
  //   } else if (minDays === 1) {
  //     str = formatMessage({
  //       defaultMessage: "1 day",
  //       description: "date text",
  //     });
  //   } else {
  //     str = formatMessage(
  //       {
  //         defaultMessage: "{minDays} days",
  //         description: "date text",
  //       },
  //       {
  //         minDays,
  //       },
  //     );
  //   }
  // } else {
  //   str = formatMessage(
  //     {
  //       defaultMessage: `{minDays}-{maxDays} days`,
  //       description: "date text",
  //     },
  //     {
  //       minDays,
  //       maxDays,
  //     },
  //   );
  // }
  /*------*/

  if (fee || feePercent) {
    const feeString = formatMessage(
      {
        defaultMessage: `{feeString} fee`,
        description: "date text",
      },
      {
        feeString: getDisplayFeeString(fee, feePercent, USDFormatter),
      },
    );

    // str += ` | ${feeString}`;
    str = feeString; // TODO: REMOVING TIMING INFO FOR NOW TILL WE SORT OUT WHAT WE WANT THIS TO SAY
  }

  return str;
};

const getPaymentMethodEntity = (paymentMethod) => {
  const isWallet = isWalletType(paymentMethod);

  if (isWallet) {
    return "wallet";
  }
  return paymentMethod.method_class;
};

const PaymentMethodIcon = ({ paymentMethod }) => {
  const entity = getPaymentMethodEntity(paymentMethod);
  return <MethodClassIcon name={entity} />;
};

const PaymentMethodName = ({ paymentMethod }) => {
  const isWallet = isWalletType(paymentMethod);
  if (isWallet) {
    return (
      <Box textAlign="right">
        <Text lineHeight={1}>
          <FormattedMessage defaultMessage="Wallet balance" description="label" />
        </Text>
        <Text fontSize="xs" color="secondaryText">
          <Money
            currency={paymentMethod.processing_currency}
            amount={paymentMethod.wallet_balance}
          />
        </Text>
      </Box>
    );
  }
  const masspayAdditionalInfoTxt =
    paymentMethod.provider === "masspay" && paymentMethod.provider_data?.display_string
      ? `${paymentMethod.provider_data.display_string}`
      : "";
  return (
    <Box>
      <Text fontSize="xs">{paymentMethod.display_string}</Text>
      {masspayAdditionalInfoTxt && (
        <Text fontSize="xs" color="secondaryText">
          {masspayAdditionalInfoTxt}
        </Text>
      )}
    </Box>
  );
};

const PayoutMethodMinDetails = ({ paymentMethod, isPayout, USDFormatter }) => {
  const details = paymentMethod.payoutOptions && paymentMethod.payoutOptions.length === 1 ? paymentMethod.payoutOptions[0] : null;
  if (!isPayout || !details) return null;
  const minFee = (details.min_fee && (
    <FormattedMessage
      defaultMessage="{minFee} minimum fee"
      description="fee descriptor"
      values={{
        minFee: USDFormatter(details.min_fee)
      }}
    />
  )) || null;
  const minAmount = (details.min_amount && (
    <FormattedMessage
      defaultMessage="{minAmount} minimum payout"
      description="minimum payout descriptor"
      values={{
        minAmount: USDFormatter(details.min_amount)
      }}
    />
  )) || null;
  if (minFee || minAmount) {
    return <Text as="i" color="secondaryText" fontSize="xs">{minFee}{minFee && minAmount && <br/>}{minAmount}</Text>; 
  }
  return null;
};

const PaymentMethod = ({
  paymentMethod,
  showMenu = true,
  onUpdate,
  onClick,
  isSelected,
  onSelect,
  onPayoutOptionSelect,
  disabled,
  isPayout,
  disabledUserMessage,
  ...props
}) => {
  const { formatMessage } = useIntl();
  const USDFormatter = useCurrencyFormatter("USD");
  const isMoreOptionsMenuVisible = showMenu && !isWalletType(paymentMethod);
  const payoutOptions = paymentMethod.payoutOptions;
  const isOnePayoutOption = payoutOptions && payoutOptions.length === 1;
  const payoutOptionDisplayString =
    payoutOptions && payoutOptions.length
      ? getPayoutOptionStr(payoutOptions[0], USDFormatter, formatMessage)
      : "";

  const firstPayoutOption = payoutOptions && payoutOptions.length ? payoutOptions[0] : "";

  const [selectedPayoutOption, setSelectedPayoutOption] = useState(firstPayoutOption);

  const handlePayoutOptionSelect = (payoutOption) => {
    setSelectedPayoutOption(payoutOption);
    if (isSelected) {
      onPayoutOptionSelect(payoutOption);
    }
  };

  const handleOnSelect = () => {
    onSelect(paymentMethod, selectedPayoutOption);
  };

  // The grid system got messy because of the amount of changes that took place
  // The CSS will need refactoring so its cleaner and better responsiveness
  return (
    <Grid
      pt="1"
      pl="5"
      pr="3"
      borderWidth="1px"
      borderRadius="4px"
      templateRows="repeat(5, 1fr)"
      templateColumns="repeat(10, 1fr)"
      alignItems="center"
      onClick={handleOnSelect}
      bgColor={isSelected ? "lightBackground" : "background"}
      borderColor={isSelected && "integrator.300"}
      opacity={disabled ? 0.5 : 1}
      pointerEvents={disabled ? "none" : "all"}
      boxShadow="base"
      data-testid={`payment-method-${paymentMethod.id}`}
      {...props}
    >
      <GridItem gridRow="1/3" gridColumn="1/11">
        <Flex justifyContent="end" alignItems="center">
          <PaymentMethodTags paymentMethod={paymentMethod} />
          {isMoreOptionsMenuVisible && (
            <MoreOptionsMenu
              paymentMethod={paymentMethod}
              onComplete={onUpdate}
              isPayout={isPayout}
            />
          )}
        </Flex>
      </GridItem>
      <GridItem gridRow="2/5" gridColumn="1/6" wordBreak="break-word">
        <VStack alignItems="flex-start" spacing="2" width="100%">
          <PaymentMethodIcon paymentMethod={paymentMethod} />
          <PaymentMethodName paymentMethod={paymentMethod} />
        </VStack>
      </GridItem>
      <GridItem gridColumn="6/10" gridRow="3/5">
          <VStack gap="0" align="end" textAlign="right">
            {payoutOptions && isOnePayoutOption ? (
                <Box textAlign="right" title={payoutOptionDisplayString}>
                  <Text fontSize="sm">{payoutOptionDisplayString}</Text>
                </Box>
              ) : payoutOptions && payoutOptions.length > 0 ? (
                <PayoutOptionSelector
                  paymentMethod={paymentMethod}
                  selectedPayoutOption={selectedPayoutOption}
                  onSelect={handlePayoutOptionSelect}
                />
              ) : null}
              <PayoutMethodMinDetails
                paymentMethod={paymentMethod}
                isPayout={isPayout}
                USDFormatter={USDFormatter}
              />
              {disabled && disabledUserMessage}
          </VStack>
      </GridItem>
      <GridItem gridRow="3/6" gridColumn="7/11" pr="2">
        <Flex justifyContent="end" alignItems="center">
          {Number.isInteger(paymentMethod.authorized_amount) ? (
            <FormattedMoney
              fontSize="lg"
              value={paymentMethod.authorized_amount}
              currency={paymentMethod.processing_currency}
            />
          ) : (
            <Text fontSize="lg">{paymentMethod.display_amount}</Text>
          )}
        </Flex>
      </GridItem>
      <Box />
    </Grid>
  );
};

export default PaymentMethod;
